import React from 'react';
import { styled } from '@compiled/react';
import round from 'lodash/round';
import { colors, fontSizeSmall } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import type { Hours, Seconds, Trend } from '../../types';
import { comparePeriodStatistics } from '../../utils/compare-period';
import { useFormatDurationFromSeconds } from '../../utils/date-range';
import { TrendIcon } from '../trend-icon';
import messages from './messages';

type Props = {
	currentDuration: Hours | Seconds;
	overallDuration: Hours | Seconds;
};

const getDescriptionTranslated = (trend: Trend, formatMessage: FormatMessage) => {
	switch (trend) {
		case 'under':
			return formatMessage(messages.underDescription);
		case 'over':
			return formatMessage(messages.overDescription);
		case 'equal':
			return formatMessage(messages.equalDescription);
		default:
			// prettier-ignore
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			(trend as never);
			throw new Error('Unknown trend is given to getDescriptionTranslated');
	}
};

export const TrendSummary = ({ currentDuration, overallDuration }: Props) => {
	const { formatMessage } = useIntl();
	const formatDurationFromSeconds = useFormatDurationFromSeconds();
	const trend = comparePeriodStatistics(currentDuration, overallDuration);
	const durationDiff = round(Math.abs(currentDuration - overallDuration), 1);

	return (
		<Wrapper>
			<TrendIconWrapper>
				<TrendIcon trend={trend} />
			</TrendIconWrapper>
			<DurationExplanation>
				{`${
					durationDiff > 0
						? formatDurationFromSeconds(durationDiff, {
								days: 'short',
								hours: 'short',
							})
						: ''
				} ${getDescriptionTranslated(trend, formatMessage)}`.trim()}
			</DurationExplanation>
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DurationExplanation = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	fontSize: `${fontSizeSmall}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N300),
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TrendIconWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
});
